import {z} from 'zod';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import {defineToolRegistryManifest} from '../../types/base-tool-types';

const toolName = 'merge-site-branch' as const;

const argumentZod = z.object({
  siteBranchId: z
    .string()
    .min(1)
    .describe(
      'Site-branch id from list-site-branches. Merges that live site-scope branch (Releases) into main.'
    ),
});

export const mergeSiteBranchManifest = defineToolRegistryManifest({
  id: toolName,
  prettyName: 'Merge site branch',
  family: 'site-branches',
  llmTool: {
    name: toolName,
    description:
      'Merges a live site-scope branch (Releases) into main for the current site. This changes main and cannot be undone. Before merging, use get-site-branch-freshness to check the branch is not behind main and list-site-branch-changes to show the user what will merge. Returns { taskId, status, nextActions }; poll the task with get-site-branch-task (taskType "branch_merge") until it finishes or fails. 409 branch_merge_in_progress while a merge is running on the site; retry after the wait in the message. 409 conflict when the branch is already merged, a publish is running, or another task holds the branch. Distinct from page-branch merges.',
    inputSchema: argumentZod,
  },
  type: 'server',
  featureGate: FEATURE_FLAGS.SITE_BRANCHES,
  requiredScopes: ['branches:merge'],
  permission: [
    {
      resource: 'SiteBranch',
      action: 'merge',
      resolveResources: (args) => [{type: 'SiteBranch', id: args.siteBranchId}],
    },
  ],
  // Rewrites main for every locale; same confirm bar as stage and publish.
  confirmBeforeExecuting: true,
  isConcurrencySafe: () => false,
  isReadOnly: () => false,
});

export type MergeSiteBranchArgumentType = z.infer<typeof argumentZod>;
